import React, { useState, useEffect } from 'react';
import { Sparkles, Brain, TrendingUp, AlertCircle, CheckCircle, Info, Loader2 } from 'lucide-react';
import api from '../services/api'; // Import your existing api service

const AutoMLSuggestion = ({ 
  datasetCharacteristics, 
  onModelSelect, 
  onParametersSelect,
  currentProblemType = 'classification',
  visible = true 
}) => {
  const [suggestions, setSuggestions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [explanation, setExplanation] = useState(null);

  // Auto-analyze when dataset characteristics change
  useEffect(() => {
    if (datasetCharacteristics && visible) {
      getSuggestions();
    }
  }, [datasetCharacteristics, currentProblemType, visible]);

  const getSuggestions = async () => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('Making AutoML request with:', {
        datasetCharacteristics,
        problemType: currentProblemType
      });

      // Use your existing api service which has the correct baseURL
      const response = await api.post('/automl/suggest', {
        datasetCharacteristics,
        problemType: currentProblemType,
        performanceGoals: 'Balanced accuracy and interpretability'
      });

      console.log('AutoML response:', response.data);
      setSuggestions(response.data.suggestions);
    } catch (err) {
      console.error('AutoML suggestion error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      });
      
      setError(err.response?.data?.message || err.message || 'Failed to get model suggestions');
    } finally {
      setLoading(false);
    }
  };

  const getExplanation = async (modelType) => {
    try {
      const response = await api.post('/automl/explain', {
        modelType,
        datasetCharacteristics,
        userLevel: 'beginner'
      });

      setExplanation(response.data.explanation);
      setShowExplanation(true);
    } catch (err) {
      console.error('Explanation error:', err);
      // Show a simple fallback explanation
      setExplanation({
        simpleExplanation: `${modelType} is recommended for your dataset type and size.`,
        technicalDetails: 'This model is suitable for your data characteristics.',
        pros: ['Good performance', 'Reliable results'],
        cons: ['May need parameter tuning'],
        whenToUse: 'For datasets like yours',
        expectedResults: 'Good baseline performance'
      });
      setShowExplanation(true);
    }
  };

  const handleModelSelect = (recommendation) => {
    setSelectedModel(recommendation.model_type);
    onModelSelect(recommendation.model_type);
    onParametersSelect(recommendation.parameters);
  };

  const getModelDisplayName = (modelType) => {
    const modelNames = {
      'random_forest': 'Random Forest',
      'decision_tree': 'Decision Tree',
      'svc': 'Support Vector Machine',
      'logistic_regression': 'Logistic Regression',
      'gradient_boosting': 'Gradient Boosting',
      'linear_regression': 'Linear Regression',
      'ridge': 'Ridge Regression',
      'lasso': 'Lasso Regression',
      'svr': 'Support Vector Regression',
      'elastic_net': 'Elastic Net',
      'mlp': 'Neural Network',
      'lstm': 'LSTM Network',
      'arima': 'ARIMA',
      'sarima': 'SARIMA',
      'prophet': 'Prophet',
      'kmeans': 'K-Means',
      'dbscan': 'DBSCAN',
      'hierarchical': 'Hierarchical Clustering'
    };
    return modelNames[modelType] || modelType;
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 0.8) return 'text-green-600 bg-green-100';
    if (confidence >= 0.6) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  if (!visible) return null;

  return (
    <div className="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-xl p-6 border border-blue-200 shadow-lg">
      {/* Header */}
      <div className="flex items-center space-x-3 mb-6">
        <div className="p-2 bg-blue-500 rounded-lg">
          <Sparkles className="w-6 h-6 text-white" />
        </div>
        <div>
          <h3 className="text-xl font-bold text-gray-800 flex items-center">
            AI Model Suggestions
            <span className="ml-2 px-2 py-1 bg-blue-500 text-white text-xs rounded-full">
              BETA
            </span>
          </h3>
          <p className="text-gray-600 text-sm">
            AI-powered recommendations based on your dataset
          </p>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500 mr-3" />
          <span className="text-gray-600">Analyzing your dataset...</span>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="flex items-center space-x-2 p-4 bg-red-100 border border-red-300 rounded-lg">
          <AlertCircle className="w-5 h-5 text-red-500" />
          <span className="text-red-700">{error}</span>
          <button 
            onClick={getSuggestions}
            className="ml-auto text-red-600 hover:text-red-800 underline"
          >
            Retry
          </button>
        </div>
      )}

      {/* Debug Info (remove in production) */}
      {process.env.NODE_ENV === 'development' && (
        <div className="mb-4 p-3 bg-gray-100 rounded text-sm">
          <strong>Debug:</strong> Problem Type: {currentProblemType}, 
          Dataset Rows: {datasetCharacteristics?.rowCount || 'N/A'}
        </div>
      )}

      {/* Suggestions */}
      {suggestions && !loading && (
        <div className="space-y-6">
          {/* Primary Recommendation */}
          <div className="bg-white rounded-lg p-5 border-2 border-blue-300 shadow-md">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-3">
                <TrendingUp className="w-6 h-6 text-blue-500" />
                <div>
                  <h4 className="font-bold text-lg text-gray-800">
                    Recommended: {getModelDisplayName(suggestions.primaryRecommendation.model_type)}
                  </h4>
                  <div className="flex items-center space-x-2 mt-1">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getConfidenceColor(suggestions.primaryRecommendation.confidence)}`}>
                      {Math.round(suggestions.primaryRecommendation.confidence * 100)}% confidence
                    </span>
                  </div>
                </div>
              </div>
              <button
                onClick={() => getExplanation(suggestions.primaryRecommendation.model_type)}
                className="p-2 text-gray-500 hover:text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"
                title="Why this model?"
              >
                <Info className="w-5 h-5" />
              </button>
            </div>

            <p className="text-gray-700 mb-4">
              {suggestions.primaryRecommendation.reasoning}
            </p>

            <div className="flex space-x-3">
              <button
                onClick={() => handleModelSelect(suggestions.primaryRecommendation)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  selectedModel === suggestions.primaryRecommendation.model_type
                    ? 'bg-blue-500 text-white'
                    : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                }`}
              >
                {selectedModel === suggestions.primaryRecommendation.model_type ? (
                  <><CheckCircle className="w-4 h-4 inline mr-2" />Selected</>
                ) : (
                  'Use This Model'
                )}
              </button>
            </div>
          </div>

          {/* Alternative Models */}
          {suggestions.alternatives && suggestions.alternatives.length > 0 && (
            <div>
              <h5 className="font-semibold text-gray-800 mb-3">Alternative Options</h5>
              <div className="space-y-3">
                {suggestions.alternatives.map((alt, index) => (
                  <div key={index} className="bg-white rounded-lg p-4 border border-gray-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <h6 className="font-medium text-gray-800">
                          {getModelDisplayName(alt.model_type)}
                        </h6>
                        <p className="text-sm text-gray-600 mt-1">{alt.reasoning}</p>
                        <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium mt-2 ${getConfidenceColor(alt.confidence)}`}>
                          {Math.round(alt.confidence * 100)}% confidence
                        </span>
                      </div>
                      <button
                        onClick={() => handleModelSelect({...alt, parameters: suggestions.primaryRecommendation.parameters})}
                        className="px-3 py-1 text-sm bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
                      >
                        Select
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Dataset Insights */}
          {suggestions.datasetInsights && suggestions.datasetInsights.length > 0 && (
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <h5 className="font-semibold text-blue-800 mb-2 flex items-center">
                <Brain className="w-4 h-4 mr-2" />
                Dataset Insights
              </h5>
              <ul className="space-y-1">
                {suggestions.datasetInsights.map((insight, index) => (
                  <li key={index} className="text-blue-700 text-sm flex items-start">
                    <span className="w-2 h-2 bg-blue-400 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                    {insight}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Performance Expectation */}
          {suggestions.performanceExpectation && (
            <div className="bg-green-50 rounded-lg p-4 border border-green-200">
              <h5 className="font-semibold text-green-800 mb-2">Expected Performance</h5>
              <p className="text-green-700 text-sm">{suggestions.performanceExpectation}</p>
            </div>
          )}

          {/* Recommendations */}
          {suggestions.recommendations && suggestions.recommendations.length > 0 && (
            <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
              <h5 className="font-semibold text-yellow-800 mb-2">Recommendations</h5>
              <ul className="space-y-1">
                {suggestions.recommendations.map((rec, index) => (
                  <li key={index} className="text-yellow-700 text-sm flex items-start">
                    <span className="w-2 h-2 bg-yellow-400 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                    {rec}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Explanation Modal */}
      {showExplanation && explanation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-800">Why This Model?</h3>
              <button
                onClick={() => setShowExplanation(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <h4 className="font-semibold text-gray-800 mb-2">Simple Explanation</h4>
                <p className="text-gray-700">{explanation.simpleExplanation}</p>
              </div>

              <div>
                <h4 className="font-semibold text-gray-800 mb-2">Advantages</h4>
                <ul className="list-disc list-inside text-gray-700 space-y-1">
                  {explanation.pros?.map((pro, index) => (
                    <li key={index}>{pro}</li>
                  )) || <li>Generally reliable performance</li>}
                </ul>
              </div>

              <div>
                <h4 className="font-semibold text-gray-800 mb-2">Limitations</h4>
                <ul className="list-disc list-inside text-gray-700 space-y-1">
                  {explanation.cons?.map((con, index) => (
                    <li key={index}>{con}</li>
                  )) || <li>May require parameter tuning</li>}
                </ul>
              </div>

              <div>
                <h4 className="font-semibold text-gray-800 mb-2">When to Use</h4>
                <p className="text-gray-700">{explanation.whenToUse}</p>
              </div>

              <div>
                <h4 className="font-semibold text-gray-800 mb-2">Expected Results</h4>
                <p className="text-gray-700">{explanation.expectedResults}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AutoMLSuggestion;